import { Loading } from "types/loadingType";

import { ICellRendererParams } from "ag-grid-community";
import {
    SpeculativeProject,
    SpeculativeProjectComponent
} from "in_queue/types/projectType";
import { Link, useNavigate } from "react-router-dom";
import { AgGridColumnDefs } from "./agGrid/agGridHelpers";
import { BaseAgGrid } from "./base/BaseAgGrid";

import css from "./TableStyles.module.scss";

// Haven't been able to find any good typings for cell renderers
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const ProjectCellRenderer: any = (
    params: ICellRendererParams<SpeculativeProject>
) => {
    const projectId = params.data?.projectId;
    return (
        <Link to={projectId ?? ""} className={css["link"]}>
            {params.data?.projectName} ({projectId})
        </Link>
    );
};

const getColumnDefs = (): AgGridColumnDefs<SpeculativeProject, never> => {
    return [
        {
            field: "projectName",
            headerName: "Project",
            pinned: true,
            width: 260,
            sort: "asc",
            cellRenderer: ProjectCellRenderer
        },
        {
            field: "fuelType",
            width: 200
        },
        {
            headerName: "Point of Interconnection",
            children: [
                {
                    field: "poi.poiType",
                    headerName: "Type",
                    width: 120
                },
                {
                    field: "poi.poiName",
                    headerName: "Location",
                    flex: 1
                }
            ]
        },
        {
            headerName: "Size (MW)",
            children: [
                {
                    field: "size.erisMw",
                    headerName: "MFO MW Energy",
                    width: 160
                },
                {
                    field: "size.nrisMw",
                    headerName: "MFO MW Capacity",
                    width: 160
                }
            ]
        },
        {
            field: "hybridComponents",
            headerName: "Hybrid Components",
            flex: 1,
            valueGetter: ({ data }) => {
                if (data === undefined) {
                    return null;
                }
                return data.hybridComponents.length > 0
                    ? data.hybridComponents.map(
                          (component: SpeculativeProjectComponent) =>
                              `${component.fuelType} - ${component.size.erisMw}MW MFO + ${component.size.nrisMw}MW CIR`
                      )
                    : `${data.fuelType} - ${data.size.erisMw}MW Energy + ${data.size.nrisMw}MW Capacity`;
            }
        }
    ];
};

export const SpeculativeProjectsTable: React.FC<{
    projects: Loading<SpeculativeProject[]>;
    height?: "auto" | number;
}> = ({ projects, height }) => {
    const navigate = useNavigate();
    const columnDefs = getColumnDefs();

    return (
        <BaseAgGrid<SpeculativeProject>
            loadingData={projects}
            columnDefs={columnDefs}
            height={height}
            rowHeight={30}
            onRowClicked={(event) => navigate(event.data?.projectId ?? "")}
            selectableRows
        />
    );
};
